/**
 * src/components/agent/SuggestionCard.jsx — Empty-state suggestion card.
 *
 * Phase 82-06 — Chat System Unification (wave 5).
 *
 * A single clickable starter-prompt card rendered inside EmptyStateSuggestions.
 * Clicking fires `onChoose(suggestion)` — the parent decides whether to fill
 * the composer or auto-send based on `suggestion.mode`.
 *
 * Design tokens only. 44px min touch. Keyboard focusable (Enter/Space).
 */

import { useState, useCallback } from 'react';
import * as Icons from 'lucide-react';

const IconFallback = Icons.Sparkles || (() => null);

function resolveIcon(name) {
  if (!name || typeof name !== 'string') return IconFallback;
  return Icons[name] || IconFallback;
}

const baseStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  width: '100%',
  minHeight: 72,
  padding: '12px 14px',
  border: '1px solid var(--bdr)',
  borderRadius: 12,
  background: 'var(--glass)',
  color: 'var(--navy)',
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'transform 120ms ease, box-shadow 120ms ease, background 120ms ease',
  boxShadow: '0 1px 2px rgba(10, 32, 68, 0.04)',
};

const iconWrapStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  flex: '0 0 auto',
  width: 36,
  height: 36,
  borderRadius: 10,
  background: 'var(--accent-tint, var(--glass))',
  color: 'var(--accent, var(--navy))',
};

const textWrapStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  minWidth: 0,
};

const titleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--navy)',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const subtitleStyle = {
  fontSize: 12,
  fontWeight: 400,
  color: 'var(--tm)',
  lineHeight: 1.3,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export default function SuggestionCard({ suggestion, onChoose }) {
  const [hover, setHover] = useState(false);
  const Icon = resolveIcon(suggestion?.icon);

  const handleClick = useCallback(() => {
    if (typeof onChoose === 'function') onChoose(suggestion);
  }, [onChoose, suggestion]);

  const handleKey = useCallback(e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  }, [handleClick]);

  const style = hover
    ? {
        ...baseStyle,
        transform: 'translateY(-1px)',
        boxShadow: '0 3px 10px rgba(10, 32, 68, 0.08)',
        background: 'var(--bg)',
      }
    : baseStyle;

  return (
    <button
      type="button"
      style={style}
      onClick={handleClick}
      onKeyDown={handleKey}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      aria-label={suggestion?.title}
      data-testid={`suggestion-card-${suggestion?.id}`}
      data-mode={suggestion?.mode || 'compose'}
    >
      <span style={iconWrapStyle} aria-hidden="true">
        <Icon size={18} strokeWidth={2} />
      </span>
      <span style={textWrapStyle}>
        <span style={titleStyle}>{suggestion?.title}</span>
        {suggestion?.subtitle && (
          <span style={subtitleStyle}>{suggestion.subtitle}</span>
        )}
      </span>
    </button>
  );
}
